import { MutableRefObject } from 'react';
import {
  GridColumnGroupingModel,
  GridColumnHeaderParams,
  GridColumnHeaderTitle,
  GridFilterItem,
} from '@mui/x-data-grid-premium';
import { TType } from '../../hooks/useTranslation';
import accounting from 'accounting-js';
import {
  defaultRenderer,
  getFormattedDate,
  textColumn,
} from '../ProjectListTable/utils';
import {
  FinancialFrame,
  FRAME_GROUPS,
} from '../../store/financialFrames/stateType';
import { EUR } from '../../constants';
import Typography from '@mui/material/Typography';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { FilterOption } from './FinancialFrameTableProps';

const findIsAnyOfItem = (
  apiRef: MutableRefObject<GridApiPremium>,
  field: string,
) =>
  apiRef.current.state.filter.filterModel.items.find(
    (item) => item.field === field && item.operator === 'isAnyOf',
  );

export const getFilterValues = (
  items: GridFilterItem[],
  field: string,
): string[] => {
  const item = items.find(
    (filterItem) =>
      filterItem.field === field && filterItem.operator === 'isAnyOf',
  );
  return Array.isArray(item?.value) ? item.value.map(String) : [];
};

export const hasSameValues = (a: string[], b: string[]) =>
  a.length === b.length && a.every((value) => b.includes(value));

// Pushes the dropdown selection into the grid filter model, reusing any item the user added from the filter panel.
export const applyFinancialFramesFilter = (
  apiRef: MutableRefObject<GridApiPremium>,
  field: string,
  id: string,
  values: string[],
) => {
  if (!apiRef.current) return;
  const existing = findIsAnyOfItem(apiRef, field);
  const existingValues = Array.isArray(existing?.value)
    ? existing.value.map(String)
    : [];
  if (hasSameValues(existingValues, values)) return;
  if (values.length) {
    apiRef.current.upsertFilterItem({
      field,
      operator: 'isAnyOf',
      value: values,
      id: existing?.id ?? id,
    });
  } else if (existing) {
    apiRef.current.deleteFilterItem(existing);
  }
};

const GRAY = 'fin-frames-gray-bg';

const isShaded = (index: number) => index % 2 === 0;

const OPEX_FIELDS: (keyof FinancialFrame)[] = FRAME_GROUPS.map(
  (group) => group.opex,
);

const CAPEX_FIELDS: (keyof FinancialFrame)[] = FRAME_GROUPS.map(
  (group) => group.capex,
);

const sumFields = (
  row: FinancialFrame,
  fields: (keyof FinancialFrame)[],
): number => fields.reduce((acc, field) => acc + (Number(row[field]) || 0), 0);

export const returnFinancialFramesNumberColumn = (
  headerName: string,
  field: string,
  className = '',
  groupLabel = '',
) => ({
  field,
  type: 'number',
  // Full name is used by the filter/columns panels; the grid header keeps the short label
  headerName: groupLabel ? `${groupLabel} (${headerName})` : headerName,
  renderHeader: (params: GridColumnHeaderParams) => (
    <GridColumnHeaderTitle
      label={headerName}
      columnWidth={params.colDef.computedWidth}
    />
  ),
  width: 120,
  display: 'flex',
  headerClassName: className,
  align: 'right',
  cellClassName: className,
  valueGetter: (value) => {
    if (value === null || value === undefined || value === '') return null;
    return typeof value === 'string' ? parseInt(value) : value;
  },
  valueFormatter: (value) =>
    value === null || value === undefined
      ? '-'
      : accounting.formatMoney(value, {
          precision: 0,
          symbol: `K${EUR}`,
          format: '%v %s',
        }),
  renderCell: (params) => (
    <Typography noWrap>{params.formattedValue}</Typography>
  ),
});

export const returnFinancialFramesTableColumns = (
  t: TType,
  countryOptions: FilterOption[],
) => [
  // Hidden column, used only for filtering by country; shows country names instead of codes in the filter panel
  {
    field: 'countryId',
    headerName: t('financialFrames.columns.country'),
    type: 'singleSelect',
    valueOptions: countryOptions,
  },
  {
    ...textColumn('countryName', t('financialFrames.columns.country'), 140),
    // Filtering by country goes through the hidden countryId column so it stays in sync with the Country dropdown
    filterable: false,
  },
  textColumn('fiscalYear', t('financialFrames.columns.fiscalYear'), 80),
  {
    field: 'updatedAt',
    headerName: t('financialFrames.columns.updated'),
    minWidth: 120,
    display: 'flex',
    valueGetter: (value) => getFormattedDate(value) || '-',
    renderCell: defaultRenderer,
  },
  ...FRAME_GROUPS.flatMap((group, index) => {
    const className = isShaded(index) ? GRAY : '';
    const groupLabel = t(`financialFrames.columns.${group.group}`);
    return [
      returnFinancialFramesNumberColumn(
        t('financialFrames.columns.opex'),
        group.opex,
        className,
        groupLabel,
      ),
      returnFinancialFramesNumberColumn(
        t('financialFrames.columns.capex'),
        group.capex,
        className,
        groupLabel,
      ),
    ];
  }),
  {
    ...returnFinancialFramesNumberColumn(
      t('financialFrames.columns.opex'),
      'totalOpex',
      GRAY,
      t('financialFrames.columns.total'),
    ),
    valueGetter: (_value, row: FinancialFrame) => sumFields(row, OPEX_FIELDS),
  },
  {
    ...returnFinancialFramesNumberColumn(
      t('financialFrames.columns.capex'),
      'totalCapex',
      GRAY,
      t('financialFrames.columns.total'),
    ),
    valueGetter: (_value, row: FinancialFrame) => sumFields(row, CAPEX_FIELDS),
  },
];

export const returnFinancialFramesColumnGroupingModel = (
  t: TType,
): GridColumnGroupingModel => [
  ...FRAME_GROUPS.map((group, index) => {
    const headerClassName = isShaded(index) ? GRAY : undefined;
    return {
      groupId: group.group,
      headerName: t(`financialFrames.columns.${group.group}`),
      ...(headerClassName ? { headerClassName } : {}),
      children: [
        { field: group.opex, ...(headerClassName ? { headerClassName } : {}) },
        { field: group.capex, ...(headerClassName ? { headerClassName } : {}) },
      ],
    };
  }),
  {
    groupId: 'total',
    headerName: t('financialFrames.columns.total'),
    headerClassName: GRAY,
    children: [
      { field: 'totalOpex', headerClassName: GRAY },
      { field: 'totalCapex', headerClassName: GRAY },
    ],
  },
];
